import React from 'react'
import { t } from 'i18next'
import { makeStyles, tokens, Text } from '@fluentui/react-components'
import { ResponsiveContainer, BarChart, XAxis, YAxis, Bar, Cell, ReferenceLine } from 'recharts'
import { GridItem } from '../common/grid/GridItem'
import { Cost, CostCategory } from '../../api/schemas/schema'
import { translateEnumValue } from '../../helpers/enumHelper'
import { getGroupedAndSummarizedCosts } from '../../helpers/riskEconomyHelper'
const useStyles = makeStyles({
   noneInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   barWrapper: {
      width: '100%',
      height: '500px',
   },
   budgetResultWrapper: {
      width: '255px',
      display: 'flex',
      justifyContent: 'space-between',
      marginLeft: '60px',
      marginBottom: tokens.spacingVerticalXXXL,
      marginTop: tokens.spacingVerticalXXXL,
   },
   lineWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
   },
   dashedLine: {
      width: '40px',
      height: '2px',
      borderTop: '3px dashed #244452',
   },
   straightLine: {
      width: '40px',
      height: '2px',
      borderTop: '3px solid #244452',
   },
})

const getBarBorderColor = (category: CostCategory) => {
   switch (category) {
      case CostCategory.DamageCosts:
         return '#FF9797'
      case CostCategory.PreventiveCosts:
         return '#7C93AB'
      case CostCategory.InsuranceCosts:
         return '#242424'
      case CostCategory.SecurityAdministration:
         return '#FFD57F'
   }
}

const getCostCategoryColor = (category: CostCategory) => {
   switch (category) {
      case CostCategory.DamageCosts:
         return '#C50F1F'
      case CostCategory.PreventiveCosts:
         return '#394B5F'
      case CostCategory.InsuranceCosts:
         return '#919191'
      case CostCategory.SecurityAdministration:
         return '#EAA300'
   }
}

const BarWithBorder = (props: any) => {
   const { x, y, width, height, borderColor } = props

   const increasedHeight = height - 4

   return (
      <g>
         <rect x={x} y={y} width={width} height={increasedHeight} stroke={borderColor || '#000'} strokeWidth={5} strokeDasharray="10 4" fillOpacity={0} />
      </g>
   )
}

const BarWithLine = (props: any) => {
   const { y, x, width, borderColor } = props

   return (
      <g>
         <rect x={x} y={y} width={width} height={1} stroke={borderColor || '#000'} strokeWidth={5} strokeDasharray="10 4" fillOpacity={0} />
      </g>
   )
}

const CustomXAxisTick = (props: any) => {
   const { x, y, payload } = props

   return (
      <text x={x} y={y} dy={20} textAnchor="middle" fontSize={tokens.fontSizeBase400} fontWeight={tokens.fontWeightMedium}>
         {translateEnumValue(CostCategory, payload.value)}
      </text>
   )
}

type FinancialYearInformationBarChartProps = {
   costs: Cost[]
}

export const FinancialYearInformationBarChart = ({ costs }: FinancialYearInformationBarChartProps) => {
   const classes = useStyles()

   const groupedCosts = getGroupedAndSummarizedCosts(costs)

   const maxDataValue = groupedCosts.length > 0 ? Math.max(...groupedCosts.map((d: any) => Math.max(d.budget || 0, d.result || 0))) : 100

   const getAxisConfig = (max: number) => {
      const rawInterval = max / 5
      const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval || 1)))
      const normalized = rawInterval / magnitude
      let interval: number
      if (normalized < 1.5) interval = magnitude
      else if (normalized < 3.5) interval = 2 * magnitude
      else if (normalized < 7.5) interval = 5 * magnitude
      else interval = 10 * magnitude

      const niceMax = Math.ceil(max / interval) * interval
      const ticks = []
      for (let i = 0; i <= niceMax; i += interval) {
         ticks.push(i)
      }
      return {
         ticks,
         domain: [0, niceMax + interval * 0.5] as [number, number],
      }
   }

   const { ticks, domain } = getAxisConfig(maxDataValue)

   return (
      <GridItem size={'9/12'} className={classes.noneInPhone}>
         <div className={classes.budgetResultWrapper}>
            <div className={classes.lineWrapper}>
               <span className={classes.dashedLine} />
               <Text size={400} weight="semibold">
                  {t('Budget')}
               </Text>
            </div>
            <div className={classes.lineWrapper}>
               <span className={classes.straightLine} />
               <Text size={400} weight="semibold">
                  {t('Result')}
               </Text>
            </div>
         </div>

         <div className={classes.barWrapper}>
            <ResponsiveContainer>
               <BarChart data={groupedCosts} margin={{ top: 25, right: 75, left: 0, bottom: 25 }}>
                  <XAxis xAxisId={0} dataKey="costCategory" tick={CustomXAxisTick} />
                  <XAxis xAxisId={1} hide />
                  <XAxis xAxisId={2} hide />
                  <YAxis ticks={ticks} domain={domain} interval={0} />

                  {ticks.filter((tick) => tick > 0).map((tick, index) => (
                     <ReferenceLine key={index} y={tick} stroke="#e0e0e0" strokeDasharray="5 5" isFront={false} />
                  ))}

                  <Bar
                     dataKey="budget"
                     xAxisId={0}
                     fillOpacity={0}
                     barSize={144}
                     shape={(props: any) =>
                        groupedCosts[props.index].budget > groupedCosts[props.index].result ? (
                           <BarWithBorder {...props} borderColor={getBarBorderColor(groupedCosts[props.index]?.costCategory)} />
                        ) : null
                     }
                  />
                  <Bar dataKey="result" xAxisId={1} fill={tokens.colorBrandBackground} barSize={150}>
                     {groupedCosts.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={getCostCategoryColor(groupedCosts[index]?.costCategory)} />
                     ))}
                  </Bar>
                  <Bar
                     dataKey="budget"
                     xAxisId={2}
                     fillOpacity={0}
                     barSize={144}
                     shape={(props: any) =>
                        groupedCosts[props.index].budget <= groupedCosts[props.index].result ? (
                           <BarWithLine {...props} borderColor={getBarBorderColor(groupedCosts[props.index]?.costCategory)} />
                        ) : null
                     }
                  />
               </BarChart>
            </ResponsiveContainer>
         </div>
      </GridItem>
   )
}
